import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import { Grid, IconButton, Input, Paper, Stack } from '@mui/material';
import {
  Add as CreateIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Done as SaveIcon,
  Edit as EditIcon,
} from '@mui/icons-material';

import { Table } from '../../../components/table/table';
import { CreateTypeModal, DeleteConfirmModal } from './components';
import { HeadCell, Row, RowCell } from '../../../components/table/types';

import {
  useCreateCompanyTypeMutation,
  useDeleteCompanyTypeMutation,
  useGetCompanyTypesQuery,
  useUpdateCompanyTypeMutation,
} from '../../../api';
import { useAppActions } from '../../../utils/hooks';
import { layoutAction } from '../../../store/sideBarStore/layoutStore';
import type { CompanyType } from '../../../types';
import { permissionsCheck } from '../../../utils/permission';

export const CompanyTypes: FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const hasPermissionToEditCopmanyTypes = permissionsCheck(['WRITE_COMPANY_TYPES']);

  // api
  const { data: companyTypes, isLoading: isCompanyTypesLoading } = useGetCompanyTypesQuery();
  const [createCompanyType, { isLoading: isCreateCompanyTypeLoading }] =
    useCreateCompanyTypeMutation();
  const [updateCompanyType, { isLoading: isUpdateCompanyTypeLoading }] =
    useUpdateCompanyTypeMutation();
  const [_deleteCompanyType, { isLoading: isDeleteCompanyTypeLoading }] =
    useDeleteCompanyTypeMutation();

  // variables
  const isLoading =
    isCompanyTypesLoading ||
    isCreateCompanyTypeLoading ||
    isUpdateCompanyTypeLoading ||
    isDeleteCompanyTypeLoading;

  // edit mode logic
  const [activeRowId, setActiveRowId] = useState<number | null>(null);
  const [name, setName] = useState('');

  const [isEditMode, setEditMode] = useState(false);
  const startEditMode = (companyType: CompanyType) => {
    setActiveRowId(companyType.id);
    setName(companyType.name);
    setEditMode(true);
  };
  const offEditMode = () => {
    setActiveRowId(null);
    setName('');
    setEditMode(false);
  };

  // edit company type handler
  const editCompanyType = async () => {
    if (!activeRowId || !name) return;

    const args = { typeId: activeRowId, body: { name } };

    try {
      await updateCompanyType(args).unwrap();
      offEditMode();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // show action button
  const { setSpeedDialButtonComponent, removeSpeedDialButtonContent } = useAppActions(layoutAction);
  useEffect(() => {
    hasPermissionToEditCopmanyTypes &&
      setSpeedDialButtonComponent([
        { icon: <CreateIcon />, onClick: openCreateModal, disabled: isEditMode },
      ]);

    return () => {
      removeSpeedDialButtonContent();
    };
  }, [isEditMode]);

  // create modal logic
  const [createModalShown, setCreateModalShown] = useState(false);
  const openCreateModal = () => {
    setCreateModalShown(true);
  };
  const closeCreateModal = () => {
    setCreateModalShown(false);
    setName('');
  };

  // delete modal logic
  const [deleteModalShown, setDeleteModalShown] = useState(false);
  const openDeleteModal = (companyType: CompanyType) => {
    setDeleteModalShown(true);
    setActiveRowId(companyType.id);
    setName(companyType.name);
  };
  const closeDeleteModal = () => {
    setDeleteModalShown(false);
    setActiveRowId(null);
    setName('');
  };

  // create company type handler
  const addCompanyType = async () => {
    if (!name) return;
    const args = { body: { name } };

    try {
      await createCompanyType(args).unwrap();
      closeCreateModal();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // delete company type handler
  const deleteCompanyType = async () => {
    if (!activeRowId) return;

    const args = { typeId: activeRowId };

    try {
      await _deleteCompanyType(args).unwrap();
      closeDeleteModal();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // table cells and rows
  const headCells: HeadCell[] = [
    { id: '1', label: t('company-types.head-cells.id') },
    { id: '2', label: t('company-types.head-cells.name') },
    {
      id: '3',
      label: t('company-types.head-cells.actions'),
      hidden: !hasPermissionToEditCopmanyTypes,
    },
  ];

  const rows: Row[] | undefined = companyTypes?.types.map((it) => {
    const { id, name: companyName } = it;

    const cells: RowCell[] = [
      { name: 'id', value: id },
      {
        name: 'name',
        value: companyName,
        component:
          isEditMode && activeRowId === id ? (
            <Input
              name="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              disabled={isUpdateCompanyTypeLoading}
            />
          ) : undefined,
      },
      {
        name: 'actions',
        hidden: !hasPermissionToEditCopmanyTypes,
        component: isEditMode ? (
          activeRowId === id ? (
            <Stack direction="row" spacing={2}>
              <IconButton
                aria-label="save"
                color="success"
                onClick={() => editCompanyType()}
                disabled={isUpdateCompanyTypeLoading}
              >
                <SaveIcon />
              </IconButton>

              <IconButton
                aria-label="cancel"
                color="error"
                onClick={() => offEditMode()}
                disabled={isUpdateCompanyTypeLoading}
              >
                <CancelIcon />
              </IconButton>
            </Stack>
          ) : undefined
        ) : (
          <Stack direction="row" spacing={2}>
            <IconButton
              aria-label="edit"
              color="warning"
              onClick={() => startEditMode(it)}
              disabled={isLoading}
            >
              <EditIcon />
            </IconButton>

            <IconButton
              aria-label="delete"
              color="error"
              onClick={() => openDeleteModal(it)}
              disabled={isLoading}
            >
              <DeleteIcon />
            </IconButton>
          </Stack>
        ),
      },
    ];

    return { cells };
  });

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Table headCells={headCells} rows={rows} />
        </Paper>
      </Grid>

      <CreateTypeModal
        open={createModalShown}
        onClose={closeCreateModal}
        onSuccess={addCompanyType}
        onCancel={closeCreateModal}
        isLoading={isLoading}
        name={name}
        setName={setName}
      />

      <DeleteConfirmModal
        open={deleteModalShown}
        onClose={closeDeleteModal}
        onSuccess={deleteCompanyType}
        onCancel={closeDeleteModal}
        isLoading={isLoading}
        name={name}
      />
    </>
  );
};
